import { Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Popover, Popper, Select, Stack } from "@mui/material";
import { Typography, Button } from "@mui/material";
import { DataGridPro, GRID_CHECKBOX_SELECTION_COL_DEF, GridToolbar,  useGridApiRef } from '@mui/x-data-grid-pro';
import * as XLSX from 'xlsx';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import CreateApi from "../../shared/Settings/appSettings";
import { useAuth } from "react-oidc-context";
import { useTBContext } from "../Contexts/useTBStates";
import type { TbRegistration, CourseTeeSetData, CourseHoleInfo, EventGroup, PlayerRosterEntry } from "@moment-golf/types";
import { ALWAYS_LOCKED_FIELDS, hasPlayerStartedAnyRound } from "./Functions/cascadeLockedFields";
import { useEffect, useState, useRef, useCallback } from "react";
import { ClickAwayListener } from "@mui/material";
import UploadTemplates from "../../shared/config/UploadTemplates.json";
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import {
    getDivisionNameOptions,
    getFlightNameOptions,
    isFlightValidForDivision,
} from "../../shared/config/registrationFieldUtils";

interface RegistrationTabProps {
    compHeights: any;
    appDims: any;
    gridHeight?: number;
    gridWidth?: number;
    //handleRegistrationUpdate: () => void;
    //registrationRef: any;
}


export const RegistrationTab = ({ compHeights, appDims, gridHeight, gridWidth }: RegistrationTabProps) => {

    const auth = useAuth();
    const api = CreateApi();
    const gridApiRef = useGridApiRef();

    const { factor, appTheme, viewEventData, viewEvent, setViewEventData,
         originalRegistrations, optionsLoaded, setOptionsLoaded, groupData } = useTBContext();
    const [selectionModel, setSelectionModel] = useState<string[]>([]);
    const [openPopper, setOpenPopper] = useState(false);
    const [fileData, setFileData] = useState<any[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [internalDragging, setInternalDragging] = useState(false);
    const [allowSave, setAllowSave] = useState(false);
    const [teeSetOptions, setTeeSetOptions] = useState<any[]>([]);

    // Import from Roster
    const [rosterGroups, setRosterGroups] = useState<EventGroup[]>([]);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [selectedPlayerKeys, setSelectedPlayerKeys] = useState<Set<string>>(new Set());
    const [importing, setImporting] = useState(false);

    // Add a ref for the grid container
    const gridRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (viewEventData.registrations.length > 0 && JSON.stringify(originalRegistrations.current) !== JSON.stringify(viewEventData.registrations)) {
            setAllowSave(true);
        }
        else {
            setAllowSave(false);
        }
    }, [viewEventData.registrations, originalRegistrations.current]);

    useEffect(() => {
        //console.log('Tee Set Check', viewEventData);
        if (viewEventData?.courseData?.length) {
            const course = viewEventData.courseData.find((c: CourseTeeSetData) => c._id === viewEvent?.courseID);
            const teeSetNames = (course?.teeSets ?? []).map((teeSet, index: number) => ({
                key: index,
                value: teeSet.teeName,
                label: teeSet.teeName + ` (${teeSet.holes.reduce((sum: number, hole: CourseHoleInfo) => sum + (hole.dist ?? 0), 0)} yds)`
            }));

            setTeeSetOptions(teeSetNames);
            //console.log('Tee Set Options', teeSetNames);
        }
    }, [viewEventData?.courseData, viewEvent?.courseID]);

    // Fetch roster groups when event has eventGroupIDs
    useEffect(() => {
        const groupIds = Array.isArray(viewEvent?.eventGroupIDs)
            ? viewEvent.eventGroupIDs
            : (viewEvent?.eventGroupID ? [viewEvent.eventGroupID] : []);

        if (!viewEvent?._id || groupIds.length === 0) {
            setRosterGroups([]);
            return;
        }

        const eventId = String(viewEvent._id);

        const eventIdsNorm = (ids: string[] | undefined) => (ids ?? []).map((x) => String(x));
        const loadGroups = async () => {
            const groups: EventGroup[] = [];
            for (const gid of groupIds) {
                const fromContext = groupData.find((g: EventGroup) => String(g._id) === String(gid));
                if (fromContext && eventIdsNorm(fromContext.eventIDs).includes(eventId) && (fromContext.playerRoster?.players?.length ?? 0) > 0) {
                    groups.push(fromContext);
                } else if (!fromContext) {
                    try {
                        const res = await api.get('/tb/GetGroupDetails', { params: { _id: gid } });
                        const g = res.data;
                        if (g && eventIdsNorm(g.eventIDs).includes(eventId) && (g.playerRoster?.players?.length ?? 0) > 0) {
                            groups.push(g);
                        }
                    } catch {
                        // ignore failed fetches
                    }
                }
            }
            setRosterGroups(groups);
        };

        loadGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- api from CreateApi() is a new ref every render; omitting prevents infinite loop
    }, [viewEvent?._id, viewEvent?.eventGroupID, viewEvent?.eventGroupIDs, groupData]);

    const playerKey = (p: PlayerRosterEntry) =>
        `${(p.playerName ?? '').trim().toLowerCase()}|${(p.playerEmail ?? '').trim().toLowerCase()}`;

    const registrationDivisionValue = (row: TbRegistration): string => {
        const value = row.division ?? row.divisionName;
        return typeof value === 'string' ? value : value != null ? String(value) : '';
    };

    const registrationFlightValue = (row: TbRegistration): string => {
        const value = row.flight ?? row.flightName;
        return typeof value === 'string' ? value : value != null ? String(value) : '';
    };

    const mergedRosterPlayers = rosterGroups.reduce<PlayerRosterEntry[]>((acc, g) => {
        for (const p of g.playerRoster?.players ?? []) {
            const key = playerKey(p);
            if (!acc.some((a) => playerKey(a) === key)) acc.push(p);
        }
        return acc;
    }, []);

    const regKeyFn = (r: { playerName?: string; playerEmail?: string }) =>
        `${(r.playerName ?? '').trim().toLowerCase()}|${(r.playerEmail ?? '').trim().toLowerCase()}`;
    const existingRegKeys = new Set(
        viewEventData.registrations
            .filter((r: TbRegistration) => !r.canceled && !r.withdrawn && !r.disqualified && !r.refunded)
            .map(regKeyFn),
    );
    const importNotRegistered = mergedRosterPlayers.filter((p) => !existingRegKeys.has(playerKey(p)));
    const importRegistered = mergedRosterPlayers.filter((p) => existingRegKeys.has(playerKey(p)));

    const canShowImportButton = rosterGroups.length > 0 && mergedRosterPlayers.length > 0;

    const handleOpenImportDialog = () => {
        setSelectedPlayerKeys(new Set(mergedRosterPlayers.map(playerKey)));
        setImportDialogOpen(true);
    };

    const handleSelectAllImport = () => setSelectedPlayerKeys(new Set(mergedRosterPlayers.map(playerKey)));
    const handleDeselectAllImport = () => setSelectedPlayerKeys(new Set());

    const handleToggleImportPlayer = (key: string) => {
        setSelectedPlayerKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const requiresTeeSet = viewEvent?.rounds?.some((r: any) => r.scoreFormat?.toLowerCase() !== 'gross') ?? false;

    const resolveTeeSetFromSmartColumns = useCallback(
        (player: PlayerRosterEntry): string | undefined => {
            if (!requiresTeeSet || !viewEventData?.courseData?.length || !viewEvent?.courseID) return player.teeSet;

            const course = viewEventData.courseData.find((c: CourseTeeSetData) => String(c._id) === String(viewEvent.courseID));
            const teeSets = course?.teeSets ?? [];
            if (teeSets.length === 0) return player.teeSet;

            const hasRating = (player.approxCourseRating ?? '') !== '';
            const hasLength = (player.approxLength ?? '') !== '';
            if (!hasRating && !hasLength) return player.teeSet;

            const genderKey = (player.gender?.toLowerCase() === 'female' ? 'womens' : 'mens') as 'womens' | 'mens';
            const teeSetsWithMeta = teeSets.map((ts: any) => ({
                ...ts,
                totalYards: (ts.holes ?? []).reduce((s: number, h: CourseHoleInfo) => s + (h.dist ?? 0), 0),
                rating: ts[`${genderKey}Rating`] ?? ts.mensRating ?? ts.womensRating ?? null,
            }));

            if (hasRating) {
                const targetRating = Number(player.approxCourseRating);
                const sorted = [...teeSetsWithMeta].sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0));
                const match = sorted.find((ts) => ts.rating != null && ts.rating <= targetRating);
                return match?.teeName;
            }
            if (hasLength) {
                const targetYards = Number(player.approxLength);
                const sorted = [...teeSetsWithMeta].sort((a, b) => b.totalYards - a.totalYards);
                const match = sorted.find((ts) => ts.totalYards <= targetYards);
                return match?.teeName;
            }
            return player.teeSet;
        },
        [requiresTeeSet, viewEventData?.courseData, viewEvent?.courseID],
    );

    /**
     * Returns all field keys defined by the event's format template (required + optional),
     * each initialised to null. This ensures new registrations have all expected keys present.
     */
    const getEventTemplateDefaults = (): Record<string, null> => {
        const matchingTemplate = UploadTemplates.find(
            t => t.playFormat === viewEvent?.playFormat &&
                 t.individual === (viewEvent?.regType?.toLowerCase() === 'single'),
        );
        const keys: string[] = [];
        if (matchingTemplate) {
            matchingTemplate.requiredFields.forEach(f => keys.push(f.key));
            matchingTemplate.optionalFields.forEach(f => { if (!keys.includes(f.key)) keys.push(f.key); });
        }
        if (viewEvent?.useTeams && !keys.includes('teamName')) keys.push('teamName');
        if (viewEvent?.useRivalry && !keys.includes('rivalryTeam')) keys.push('rivalryTeam');
        return Object.fromEntries(keys.map(k => [k, null])) as Record<string, null>;
    };

    /**
     * Maps a roster player to registration fields, returning only values that are
     * actually present on the player (non-null, non-empty). Core identity fields are
     * always included. teeSet is smart-resolved when possible.
     */
    const rosterEntryToRegFields = (p: PlayerRosterEntry): Record<string, unknown> => {
        const teeSet = resolveTeeSetFromSmartColumns(p) ?? p.teeSet;
        const fields: Record<string, unknown> = {
            playerName: p.playerName ?? '',
            playerEmail: p.playerEmail ?? '',
            userID: p.userID ?? null,
        };
        const candidates: Array<[string, unknown]> = [
            ['playerPhone', p.playerPhone],
            ['address', p.address],
            ['city', p.city],
            ['state', p.state],
            ['zip', p.zip],
            ['playerHandicap', p.playerHandicap],
            ['ghinNumber', p.ghinNumber],
            ['gender', p.gender],
            ['division', p.division],
            ['flight', p.flight],
            ['teamName', p.teamName],
            ['teeSet', teeSet],
        ];
        for (const [key, val] of candidates) {
            if (val != null && val !== '') fields[key] = val;
        }
        return fields;
    };

    const handleImportFromRoster = async () => {
        const selected = mergedRosterPlayers.filter((p) => selectedPlayerKeys.has(playerKey(p)));
        if (selected.length === 0) {
            setImportDialogOpen(false);
            return;
        }

        setImporting(true);
        const regKey = (r: { playerName?: string; playerEmail?: string }) =>
            `${(r.playerName ?? '').trim().toLowerCase()}|${(r.playerEmail ?? '').trim().toLowerCase()}`;
        const existingRegs = viewEventData.registrations.filter(
            (r: TbRegistration) => !r.canceled && !r.withdrawn && !r.disqualified && !r.refunded,
        );

        const toCreate: any[] = [];
        const toUpdate: any[] = [];

        const templateDefaults = getEventTemplateDefaults();

        for (const p of selected) {
            const key = playerKey(p);
            const existing = existingRegs.find((r) => regKey(r) === key);
            const rosterFields = rosterEntryToRegFields(p);

            if (existing) {
                // For updates: only apply non-null roster values to avoid overwriting existing data
                toUpdate.push({ ...existing, ...rosterFields });
            } else {
                // For creates: seed all event template fields as null, then overlay roster values
                const base: any = {
                    ...templateDefaults,
                    ...rosterFields,
                    eventID: viewEvent?._id,
                    scoreCode: Math.random().toString(36).substring(2, 10).toUpperCase(),
                    createdBy: auth.user?.profile.sub,
                    modifiedBy: auth.user?.profile.sub,
                };
                toCreate.push(base);
            }
        }

        try {
            let combined = [...viewEventData.registrations];

            if (toCreate.length > 0) {
                let newRegs = [...toCreate];
                if (viewEvent?.regType?.toLowerCase() !== 'single') {
                    const groupKeys = toCreate.map((r, i) =>
                        r.groupNumber != null && r.groupNumber !== '' ? r.groupNumber : `__idx_${i}`,
                    );
                    const uniqueKeys = Array.from(new Set(groupKeys));
                    const scoreCodeMap = Object.fromEntries(
                        uniqueKeys.map((k) => [k, Math.random().toString(36).substring(2, 10).toUpperCase()]),
                    );
                    newRegs = toCreate.map((r, i) => ({
                        ...r,
                        scoreCode: scoreCodeMap[groupKeys[i]] ?? r.scoreCode,
                    }));
                }
                const res = await api.post('/tb/UploadRegistrations', newRegs);
                const withIds = (res.data ?? []) as TbRegistration[];
                combined = [...combined, ...withIds];
            }

            if (toUpdate.length > 0) {
                const cleaned = toUpdate.map((u) => {
                    const { hasUserID, hasEventScoreID, ...rest } = u as any;
                    return rest;
                });
                await api.post('/tb/UpdateRegistrations', {
                    user_userID: auth.user?.profile.sub,
                    registrations: cleaned,
                });
                for (const u of toUpdate) {
                    const idx = combined.findIndex((r) => r._id === u._id);
                    if (idx >= 0) combined[idx] = { ...combined[idx], ...u } as TbRegistration;
                }
            }

            setViewEventData({ ...viewEventData, registrations: combined });
            originalRegistrations.current = JSON.parse(JSON.stringify(combined));
            setImportDialogOpen(false);
            setSelectedPlayerKeys(new Set());
        } catch (err) {
            console.error('Import from roster failed:', err);
        } finally {
            setImporting(false);
        }
    };

    const handleRegistrationUpdate = async () => {

        const saveRegistrations: any[] = [];
        const updatedRegistrations = JSON.parse(JSON.stringify(viewEventData.registrations));

        //console.log('Updated Registrations', updatedRegistrations);
        //console.log('Original Registrations', originalRegistrations.current);

        if (JSON.stringify(originalRegistrations.current) !== JSON.stringify(updatedRegistrations)) {

            originalRegistrations.current.forEach((registration: TbRegistration) => {
                const singleUpdatedRegistration = updatedRegistrations.find((reg: TbRegistration) => reg._id === registration._id);
                
                if (JSON.stringify(singleUpdatedRegistration) !== JSON.stringify(registration)) {

                    delete singleUpdatedRegistration.hasUserID;
                    delete singleUpdatedRegistration.hasEventScoreID;

                    saveRegistrations.push(singleUpdatedRegistration);
                }
            });

            //console.log('Save Registrations', saveRegistrations);

            api.post('/tb/UpdateRegistrations', {
                user_userID: auth.user?.profile.sub,
                registrations: saveRegistrations
            })
            .then((res: any) => {
                console.log(res);
            })
            .catch((err: any) => {
                console.error(err);
            });  

        }

        originalRegistrations.current = JSON.parse(JSON.stringify(viewEventData.registrations));
        setAllowSave(false);
    }

    const deleteSelectedRegistrations = () => {
        const selectedRows = gridApiRef.current?.getSelectedRows();
        const selectedData = Array.from(selectedRows?.values() || []);

        api.post('/tb/DeleteRegistrations', {
            registrations: selectedData
        })
        .then((res: any) => {
            //console.log(res);
        })
        .catch((error: any) => {
            console.error('Error deleting registrations:', error);
        });

        const updatedRegistrations = viewEventData.registrations.filter((reg: TbRegistration) =>
            !selectedData.some((id: unknown) => (id as { _id?: string })._id === reg._id)
        );

        setViewEventData({...viewEventData, registrations: updatedRegistrations});
        originalRegistrations.current = JSON.parse(JSON.stringify(updatedRegistrations));
    }

    const readFile = async (file: File) => {
        const reader = new FileReader();
        
        reader.onload = async (e) => {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });

            // Assuming you want to read the first sheet
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            // Convert the sheet to JSON format with headers
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
                raw: true,  // Keep raw values
                defval: null // Default value for empty cells
            });

            // Get the template for the current event
            const template = getTemplate();
            
            if (template && jsonData.length > 0) {
                // Create a mapping from Excel column names (lowercase) to our field keys
                const fieldMapping: any = {};
                
                // Add required fields to mapping
                template.requiredFields.forEach((field: any) => {
                    fieldMapping[field.label.toLowerCase()] = field.key;
                });
                
                // Add optional fields to mapping
                template.optionalFields.forEach((field: any) => {
                    fieldMapping[field.label.toLowerCase()] = field.key;
                });
                
                // Transform the data by replacing keys and skipping empty columns
                const formattedData = jsonData.map((row: any) => {
                    const newRow: any = {};
                    
                    // Process each key in the row
                    Object.entries(row).forEach(([excelKey, value]: any) => {
                        // Skip empty columns
                        if (excelKey !== '__EMPTY') {
                            // Try to find a matching field in our mapping
                            const mappedKey = fieldMapping[excelKey.toLowerCase()];
                            
                            if (mappedKey) {
                                // Use the mapped key from our template
                                newRow[mappedKey] = value;
                            } else {
                                // Keep the original key if no mapping found
                                newRow[excelKey] = value;
                            }
                        }
                    });
                    
                    return newRow;
                });
                
                //console.log('Formatted Data', formattedData);
                setFileData(formattedData);
            } else {
                // If no template or no data, just set the original data
                setFileData(jsonData as any[]);
            }
        };
        
        reader.readAsArrayBuffer(file);
    }

    const handleFileUpload = async () => {
        
        let registrations = [...fileData];

        // Will need to update to handle all variations of pairs/teams
        if (viewEvent?.regType?.toLowerCase() !== 'single') {
            const groupNumbers = new Set(registrations.map(row => row.groupNumber));
            const scoreCodes = Array.from(groupNumbers).map((groupNumber, index) => ({
                groupNumber,
                scoreCode: Math.random().toString(36).substring(2, 10).toUpperCase() // Generate a random alphanumeric string with 8 characters, all upper case
            }));

            const scoreCodeMap = Object.fromEntries(scoreCodes.map(({ groupNumber, scoreCode }) => [groupNumber, scoreCode]));

            const updatedRegistrations = registrations.map(row => ({
                ...row,
                scoreCode: scoreCodeMap[row.groupNumber] || row.scoreCode, // Assign the generated score code
                eventID: viewEvent?._id,
                createdBy: auth.user?.profile.sub,
                modifiedBy: auth.user?.profile.sub
            }));

            registrations = updatedRegistrations;
        } else {
            registrations = registrations.map(row => ({
                ...row,
                eventID: viewEvent?._id,
                scoreCode: Math.random().toString(36).substring(2, 10).toUpperCase(),
                createdBy: auth.user?.profile.sub,
                modifiedBy: auth.user?.profile.sub
            }));
        }

        //console.log('Registrations', registrations);

        // Send params to your API
        await api.post('/tb/UploadRegistrations', registrations)
        .then((res: any) => {
            //console.log(res.data);

            const updatedRegistrations = [...viewEventData.registrations, ...res.data];
            setViewEventData({...viewEventData, registrations: updatedRegistrations});
        })
        .catch((error: any) => {
            console.error('Error uploading data:', error);
        });

    }

    const handleTemplateDownload = () => {

        const xlsxSheetData: any[] = [];
        //const uploadTemplates = require('../../shared/config/UploadTemplates.json'); // Assuming the path to the UploadTemplates.json file
        const matchingTemplate = UploadTemplates.find(template => 
            template.playFormat === viewEvent?.playFormat &&
            template.individual === (viewEvent?.regType?.toLowerCase() === 'single')
        );

        if (matchingTemplate) {
            const requiredFields = matchingTemplate.requiredFields.map(field => ({
                [field.key]: ''
            }));
            const optionalFields = matchingTemplate.optionalFields.map(field => ({
                [field.key]: ''
            }));
            xlsxSheetData.push(...requiredFields, ...optionalFields, {teamName: ''});
        } else {
            xlsxSheetData.push({ NoTemplateFound: '' });
        }
        /* const headers = templateHeaders.filter((header: any) => header.playType === viewEvent.playType)[0]?.value;

        console.log(viewEvent.playType);
        console.log(headers);

        if (headers) {
            headers.forEach((header: any) => {
                xlsxSheetData.push({
                    [header.label]: ''
                });
            });
        } */

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.json_to_sheet(xlsxSheetData || [{NoTemplateFound: ''}]);

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, "Template");

        // Generate and download file
        XLSX.writeFile(wb, `${viewEvent?.playFormat}_${viewEvent?.playType}_template.xlsx`);
    }

    const handleDataFieldDrop = (data: string, currentField: string) => {
        //console.log('Data Dropped', data);

        const field = JSON.parse(data);

        const newFileData = fileData.map((row: any) => {
            const newRow: any = {};
            Object.keys(row).forEach((key: string) => {
                if (key === currentField) {
                    newRow[field.key] = row[key];
                } else {
                    newRow[key] = row[key];
                }
            });
            return newRow;
        });

        //console.log('New File Data', newFileData);
        setFileData(newFileData);
    }

    const getTemplate = () => {

        //console.log('Play Format', viewEvent?.playFormat);
        //console.log('Reg Type boolean', viewEvent?.regType?.toLowerCase() === 'single');
        
        const matchingTemplate = UploadTemplates.find(template => 
            template.playFormat === viewEvent?.playFormat &&
            template.individual === (viewEvent?.regType?.toLowerCase() === 'single')
        );

        // Check if the teamName field already exists in requiredFields or optionalFields
        const teamNameField = { key: 'teamName', label: 'Team Name', type: 'text' };

        if (viewEvent?.useTeams) {
            if (!matchingTemplate?.requiredFields.some(field => field.key === teamNameField.key)) {
                matchingTemplate?.requiredFields.push(teamNameField);
            }
        } else {
            if (!matchingTemplate?.optionalFields.some(field => field.key === teamNameField.key)) {
                matchingTemplate?.optionalFields.push(teamNameField);
            }
        }

        //console.log('Matching Template', matchingTemplate);
        return JSON.parse(JSON.stringify(matchingTemplate));
    }

    const createColumns = () => {
        const defaultTemplate = {playerName: 'me', playerEmail: 'me@me.com'};
        const columns: any[] = [];
        const template = getTemplate();
        const noShows = ['createdAt', 'modifiedAt', 'createdBy', 'modifiedBy', 'userID',
             'eventID', 'teeTimes', 'method', 'eventScoreID', 'divisionName', 'flightName'];

        const regs = viewEventData.registrations;
        const orderedKeys: string[] = [];
        if (regs.length === 0) {
            orderedKeys.push(...Object.keys(defaultTemplate));
        } else {
            const seen = new Set<string>();
            for (const reg of regs) {
                for (const key of Object.keys(reg)) {
                    if (!seen.has(key)) {
                        seen.add(key);
                        orderedKeys.push(key);
                    }
                }
            }
        }

        const sampleForKey = (key: string): unknown => {
            const rows = regs.length > 0 ? regs : [defaultTemplate];
            for (const reg of rows) {
                const v = (reg as Record<string, unknown>)[key];
                if (v !== undefined) return v;
            }
            return undefined;
        };

        orderedKeys.forEach((key: string) => {
            const sample = sampleForKey(key);
            const inferredJsType = sample !== undefined ? typeof sample : 'string';

            if (key === '_id') {
                columns.push({
                    field: key,
                    headerName: 'ID',
                    minWidth: 100,
                    flex: 1,
                    editable: false,
                    hideable: false,
                    headerClassName: 'custom-header'
                });
                return;
            }

            if (['hasUserID', 'hasEventScoreID'].includes(key)) {
                columns.push({
                    field: key,
                    headerName: key === 'hasUserID' ? 'User?' : 'Score?',
                    minWidth: 70,
                    width: 70,
                    type: 'boolean',
                    //flex: 1,
                    resizable: false,
                    editable: false,
                    hideable: false,
                    sortable: false,
                    disableColumnMenu: true,
                    headerClassName: 'custom-header'
                });
                return;
            }

            if (key === 'teeSet') {
                //console.log('Tee Set Check in Select', teeSetOptions);
                columns.push({
                    field: key, 
                    headerName: "Tee Set",
                    minWidth: 100,
                    flex: 1,
                    type: 'singleSelect',
                    valueOptions: teeSetOptions,
                    editable: true,
                    filterable: true,
                    sortable: true,
                    hideable: true,
                    headerClassName: 'custom-header',
                });
                return;
            }

            if (key === 'division') {
                columns.push({
                    field: 'division',
                    headerName: 'Division',
                    minWidth: 120,
                    flex: 1,
                    type: 'singleSelect',
                    valueOptions: getDivisionNameOptions(viewEvent),
                    valueGetter: (_value: unknown, row: TbRegistration) =>
                        registrationDivisionValue(row),
                    editable: true,
                    filterable: true,
                    sortable: true,
                    hideable: true,
                    headerClassName: 'custom-header',
                });
                return;
            }

            if (key === 'flight') {
                columns.push({
                    field: 'flight',
                    headerName: 'Flight',
                    minWidth: 120,
                    flex: 1,
                    type: 'singleSelect',
                    valueOptions: (params: { row: TbRegistration }) =>
                        getFlightNameOptions(viewEvent, registrationDivisionValue(params.row) || null),
                    valueGetter: (_value: unknown, row: TbRegistration) =>
                        registrationFlightValue(row),
                    editable: true,
                    filterable: true,
                    sortable: true,
                    hideable: true,
                    headerClassName: 'custom-header',
                });
                return;
            }



            if (template?.requiredFields.find((field: any) => field.key === key)) {
                // Checks and pushes required fields to columns
                const field = template?.requiredFields.find((field: any) => field.key === key);
                    
                columns.push({ 
                    field: field?.key, 
                    headerName: field?.label,
                    minWidth: 100,
                    flex: 1,
                    type: field?.type || inferredJsType || 'string',
                    editable: true,
                    filterable: true,
                    sortable: true,
                    hideable: true,
                    headerClassName: 'custom-header',
                });
                return;
            }

            if (template?.optionalFields.find((field: any) => field.key === key)) {
                const field = template?.optionalFields.find((field: any) => field.key === key);
                columns.push({ 
                    field: field?.key, 
                    headerName: field?.label,
                    minWidth: 100,
                    flex: 1,
                    type: field?.type || inferredJsType || 'string',
                     editable: true,
                    filterable: true,
                    sortable: true,
                    hideable: true,
                    headerClassName: 'custom-header',
                });
                return;
            }

            
            if (!noShows.includes(key)) {
                columns.push({
                    field: key,
                    headerName: key,
                    minWidth: 100,
                    flex: 1,
                    type: inferredJsType,
                    editable: true,
                    filterable: true,
                    sortable: true,
                    hideable: true,
                    headerClassName: 'custom-header'
                });
                return;
            }

        });

        // When event has flighting divisions, always show Division and Flight columns
        const flightingDivisions = (viewEvent?.flighting?.divisions ?? []) as any[];
        const flightingFlights = (viewEvent?.flighting?.flights ?? []) as any[];
        const hasDivisions = flightingDivisions.length > 0;
        const hasFlights = flightingFlights.length > 0;

        if (hasDivisions && !columns.some((c: any) => c.field === 'division')) {
            const playerEmailIdx = columns.findIndex((c: any) => c.field === 'playerEmail');
            const insertIdx = playerEmailIdx >= 0 ? playerEmailIdx + 1 : columns.length;
            columns.splice(insertIdx, 0, {
                field: 'division',
                headerName: 'Division',
                minWidth: 120,
                flex: 1,
                type: 'singleSelect',
                valueOptions: getDivisionNameOptions(viewEvent),
                valueGetter: (_value: unknown, row: TbRegistration) =>
                    registrationDivisionValue(row),
                editable: true,
                filterable: true,
                sortable: true,
                hideable: true,
                headerClassName: 'custom-header',
            });
        }

        if (hasFlights && !columns.some((c: any) => c.field === 'flight')) {
            const divisionIdx = columns.findIndex((c: any) => c.field === 'division');
            const playerEmailIdx = columns.findIndex((c: any) => c.field === 'playerEmail');
            const anchorIdx = divisionIdx >= 0 ? divisionIdx : playerEmailIdx;
            const insertIdx = anchorIdx >= 0 ? anchorIdx + 1 : columns.length;
            columns.splice(insertIdx, 0, {
                field: 'flight',
                headerName: 'Flight',
                minWidth: 120,
                flex: 1,
                type: 'singleSelect',
                valueOptions: (params: { row: TbRegistration }) =>
                    getFlightNameOptions(viewEvent, registrationDivisionValue(params.row) || null),
                valueGetter: (_value: unknown, row: TbRegistration) =>
                    registrationFlightValue(row),
                editable: true,
                filterable: true,
                sortable: true,
                hideable: true,
                headerClassName: 'custom-header',
            });
        }

        // When event uses teams but registrations have no teamName data, show Team Name column so users can add it
        if (viewEvent?.useTeams && !columns.some((c: any) => c.field === 'teamName')) {
            const teamNameColumn = {
                field: 'teamName',
                headerName: 'Team Name',
                minWidth: 100,
                flex: 1,
                type: 'string',
                editable: true,
                filterable: true,
                sortable: true,
                hideable: true,
                headerClassName: 'custom-header',
            };
            const playerEmailIdx = columns.findIndex((c: any) => c.field === 'playerEmail');
            const insertIdx = playerEmailIdx >= 0 ? playerEmailIdx + 1 : columns.length;
            columns.splice(insertIdx, 0, teamNameColumn);
        }

        // Always ensure Team Name column stays after playerEmail (main loop may add it at end when reg has teamName)
        if (viewEvent?.useTeams) {
            const teamNameIdx = columns.findIndex((c: any) => c.field === 'teamName');
            const playerEmailIdx = columns.findIndex((c: any) => c.field === 'playerEmail');
            const targetIdx = playerEmailIdx >= 0 ? playerEmailIdx + 1 : columns.length;
            if (teamNameIdx >= 0 && teamNameIdx !== targetIdx) {
                const [col] = columns.splice(teamNameIdx, 1);
                const newIdx = targetIdx > teamNameIdx ? targetIdx - 1 : targetIdx;
                columns.splice(newIdx, 0, col);
            }
        }

        // When event uses rivalry, always show Rivalry Team column with team name options
        if (viewEvent?.useRivalry && viewEvent?.rivalry) {
            const rivalryTeamOptions = [
                viewEvent.rivalry.teamA?.name,
                viewEvent.rivalry.teamB?.name,
            ].filter(Boolean) as string[];

            if (!columns.some((c: any) => c.field === 'rivalryTeam')) {
                const playerEmailIdx = columns.findIndex((c: any) => c.field === 'playerEmail');
                const insertIdx = playerEmailIdx >= 0 ? playerEmailIdx + 1 : columns.length;
                columns.splice(insertIdx, 0, {
                    field: 'rivalryTeam',
                    headerName: 'Rivalry Team',
                    minWidth: 140,
                    flex: 1,
                    type: 'singleSelect',
                    valueOptions: rivalryTeamOptions,
                    editable: true,
                    filterable: true,
                    sortable: true,
                    hideable: true,
                    headerClassName: 'custom-header',
                });
            } else {
                // Ensure it stays positioned after playerEmail
                const rivalryTeamIdx = columns.findIndex((c: any) => c.field === 'rivalryTeam');
                const playerEmailIdx = columns.findIndex((c: any) => c.field === 'playerEmail');
                const targetIdx = playerEmailIdx >= 0 ? playerEmailIdx + 1 : columns.length;
                if (rivalryTeamIdx >= 0 && rivalryTeamIdx !== targetIdx) {
                    const [col] = columns.splice(rivalryTeamIdx, 1);
                    const newIdx = targetIdx > rivalryTeamIdx ? targetIdx - 1 : targetIdx;
                    columns.splice(newIdx, 0, col);
                }
            }
        }

        //console.log('Columns', columns);
        return columns;
    }

    const getRowClassName = (params: any) => {
        const registration = params.row;
        
        // Check status in priority order (refunded first, then canceled, withdrawn, disqualified)
        if (registration.refunded) {
            return 'row-refunded';
        } else if (registration.canceled) {
            return 'row-canceled';
        } else if (registration.withdrawn) {
            return 'row-withdrawn';
        } else if (registration.disqualified) {
            return 'row-disqualified';
        }
        
        // Active registration - no background color
        return 'row-active';
    };

    const templateButton = () => {
        return (
            <Button
                onClick={handleTemplateDownload}
                sx = {{
                    width: 'max-content',
                    borderRadius: 2,
                    padding: 0.5,
                    justifyContent: 'flex-start',
                    border: '1px solid transparent',
                    '&:hover': {border: `1px solid ${appTheme.palette.primary.extraLight}`},
                    '&:active': {backgroundColor: 'transparent'},
                    '&:focus': {backgroundColor: 'transparent'},
                }}
            >
                <Typography sx={{ fontWeight: 500, fontSize: 14*factor, display: 'flex', alignItems: 'center', textAlign: 'left', justifyItems: 'center' }}>
                    <DownloadIcon sx={{ fontSize: 16*factor }} />{viewEvent?.playFormat}{viewEvent?.useTeams ? ' w/ Teams' : ''} Template
                </Typography>
            </Button>
        )
    }
    
    return (
        <Stack direction='column' spacing={1} sx={{ flex: 1, width: '100%', height: '100%', minHeight: 0, boxSizing: 'border-box' }}>
            <Stack direction='row' spacing={1} alignItems='center' justifyContent='space-between' sx={{ flexShrink: 0 }}>
                <Stack direction='row' spacing={3} alignItems='center'>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <Typography sx={{ fontWeight: 500, fontSize: 14*factor }}># Registrations:</Typography>
                        <Typography sx={{ fontWeight: 500, fontSize: 14*factor }}>{
                        viewEventData?.registrations?.filter((reg: TbRegistration) => !reg.canceled && !reg.withdrawn && !reg.disqualified && !reg.refunded).length}</Typography>
                    </Stack>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <Typography sx={{ fontWeight: 500, fontSize: 14*factor }}># Paid:</Typography>
                        <Typography sx={{ fontWeight: 500, fontSize: 14*factor }}>{viewEventData?.registrations?.filter((reg: TbRegistration) => reg.paid).length}</Typography>
                    </Stack>
                </Stack>
                <Stack direction='row' spacing={3} alignItems='center'>
                    {!allowSave && selectionModel.length > 0 && 
                        <Button onClick={deleteSelectedRegistrations}
                            sx = {{
                                width: 'max-content',
                                borderRadius: 2,
                                padding: 0.5,
                                justifyContent: 'flex-start',
                                border: '1px solid transparent',
                                '&:hover': {border: `1px solid ${appTheme.palette.primary.extraLight}`},
                                '&:active': {backgroundColor: 'transparent'},
                                '&:focus': {backgroundColor: 'transparent'},
                            }}
                        >Delete
                        </Button>
                    }
                    {allowSave && 
                        <Button 
                            onClick={handleRegistrationUpdate} 
                            sx={{ 
                                width: 'max-content', 
                                borderRadius: 2, 
                                padding: 0.5, 
                                justifyContent: 'flex-start', 
                                border: '1px solid transparent', 
                                '&:hover': {border: `1px solid ${appTheme.palette.primary.extraLight}`}, 
                                '&:active': {backgroundColor: 'transparent'}, 
                                '&:focus': {backgroundColor: 'transparent'}, 
                            }}
                        >Save Changes</Button> 
                    }
                    {canShowImportButton && (
                        <Button
                            onClick={handleOpenImportDialog}
                            sx={{
                                width: 'max-content',
                                borderRadius: 2,
                                padding: 0.5,
                                justifyContent: 'flex-start',
                                border: '1px solid transparent',
                                '&:hover': { border: `1px solid ${appTheme.palette.primary.extraLight}` },
                                '&:active': { backgroundColor: 'transparent' },
                                '&:focus': { backgroundColor: 'transparent' },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 500,
                                    fontSize: 14 * factor,
                                    display: 'flex',
                                    alignItems: 'center',
                                    textAlign: 'left',
                                    justifyItems: 'center',
                                }}
                            >
                                <GroupAddIcon sx={{ fontSize: 16 * factor }} /> Import from Roster
                            </Typography>
                        </Button>
                    )}
                    {templateButton()}
                    <Button component="label"
                        sx = {{
                            width: 'max-content',
                            borderRadius: 2,
                            padding: 0.5,
                            justifyContent: 'flex-start',
                            border: '1px solid transparent',
                            '&:hover': {border: `1px solid ${appTheme.palette.primary.extraLight}`},
                            '&:active': {backgroundColor: 'transparent'},
                            '&:focus': {backgroundColor: 'transparent'},
                        }}
                        onClick={() => setOpenPopper(true)}
                    >
                        <Typography 
                            sx={{ 
                                fontWeight: 500, 
                                fontSize: 14*factor, 
                                display: 'flex', 
                                alignItems: 'center', 
                                textAlign: 'left', 
                                justifyItems: 'center' }}>
                            <UploadIcon sx={{ fontSize: 16*factor }} /> Registrations
                        </Typography>
                        {/* <input type="file" hidden onChange={(e) => {
                            const files = e.target.files;
                            if (files && files[0]) {
                                //readFile(files[0]);
                                //handleFileUpload(files[0]);
                                resetFileInput(e.target);
                            }
                        }} /> */}
                    </Button>
                </Stack>
            </Stack>

            <Box 
                ref={gridRef}
                sx={{ 
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                    height: gridHeight ?? '100%',
                    backgroundColor: 'sepia.background',
                }}
            >
                
                <DataGridPro
                    apiRef={gridApiRef}
                    rows={viewEventData.registrations}
                    getRowId={(row) => row._id}
                    columns={createColumns()}
                    getRowClassName={getRowClassName}
                    isCellEditable={(params) => {
                        if (ALWAYS_LOCKED_FIELDS.includes(params.field)) {
                            return !hasPlayerStartedAnyRound(viewEventData.scores, params.row.scoreCode);
                        }
                        return true;
                    }}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                _id: false,
                            },
                            
                        },
                        pinnedColumns: {
                            left: [GRID_CHECKBOX_SELECTION_COL_DEF.field,'playerName']
                        }
                    }}
                    density='compact'
                    pinnedColumns={{
                        left: [GRID_CHECKBOX_SELECTION_COL_DEF.field,'playerName']
                    }}
                    checkboxSelection
                    slots={{
                        toolbar: GridToolbar
                    }}  
                    editMode='row'
                    onRowEditStop={(params) => {
                        console.log(params);
                    }}
                    processRowUpdate={(params) => {
                        const originalRow = viewEventData.registrations.find((r) => r._id === params._id);
                        let updatedParams = { ...params } as TbRegistration;

                        const divisionChanged = originalRow && originalRow.division !== params.division;
                        if (divisionChanged) {
                            updatedParams.divisionName = params.division ?? undefined;
                            const flightName = registrationFlightValue(params);
                            if (flightName && !isFlightValidForDivision(viewEvent, params.division, flightName)) {
                                updatedParams.flight = undefined;
                                updatedParams.flightName = undefined;
                            }
                        } else if (originalRow && originalRow.flight !== params.flight) {
                            updatedParams.flightName = params.flight ?? undefined;
                        }

                        const isGroupEvent = viewEvent?.regType?.toLowerCase() !== 'single';
                        const groupNumberChanged = originalRow && originalRow.groupNumber !== params.groupNumber;

                        if (isGroupEvent && groupNumberChanged) {
                            const newGroupNumber = params.groupNumber;

                            if (newGroupNumber == null || String(newGroupNumber ?? '') === '') {
                                updatedParams = {
                                    ...updatedParams,
                                    scoreCode: Math.random().toString(36).substring(2, 10).toUpperCase(),
                                };
                            } else {
                                const match = viewEventData.registrations.find(
                                    (r) => r._id !== params._id && Number(r.groupNumber) === Number(newGroupNumber)
                                );
                                if (match) {
                                    updatedParams = { ...updatedParams, scoreCode: match.scoreCode ?? updatedParams.scoreCode };
                                } else {
                                    updatedParams = {
                                        ...updatedParams,
                                        scoreCode: Math.random().toString(36).substring(2, 10).toUpperCase(),
                                    };
                                }
                            }
                        }

                        const updatedRows = viewEventData.registrations.map((row: TbRegistration) =>
                            row._id === params._id ? updatedParams : row
                        );
                        setViewEventData({ ...viewEventData, registrations: updatedRows });
                        return updatedParams;
                    }}
                    onProcessRowUpdateError={(params) => {
                        console.log('Error', params);
                       
                    }}
                    onRowSelectionModelChange={(newSelectionModel) => {
                        setSelectionModel(newSelectionModel as string[]);
                    }}
                    sx={{ 
                        marginTop: 1, 
                        fontSize: 12*factor, 
                        width: gridWidth ?? '100%',
                        height: '100%',
                        backgroundColor: 'sepia.background',
                        "& .MuiDataGrid-main": {
                            backgroundColor: 'sepia.background',
                        },
                        "& .MuiDataGrid-virtualScroller": {
                            backgroundColor: 'sepia.background',
                        },
                        "& .MuiDataGrid-toolbarContainer": {
                            backgroundColor: 'sepia.background',
                        },
                        "& .MuiDataGrid-columnHeader": {
                            backgroundColor: 'sepia.background',
                        },
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontWeight: 500,
                            fontSize: 12*factor,
                        },
                        "& .MuiDataGrid-iconButtonContainer": {
                            backgroundColor: 'transparent',
                            padding: 0,
                            margin: 0,
                            width: 15,
                            display: 'none'
                        },
                        "& .MuiDataGrid-sortIcon": {
                            width: 15,
                            height: 15,
                            color: 'red',
                            display: 'none'

                        },
                        // Row highlighting styles based on registration status
                        "& .row-canceled": {
                            backgroundColor: 'rgb(255, 153, 153)',
                            color: 'rgba(0, 0, 0, 0.87)',
                            "&:hover": {
                                backgroundColor: 'rgb(255, 179, 179)',
                                color: 'rgba(0, 0, 0, 0.87)',
                            },
                        },
                        "& .row-withdrawn": {
                            backgroundColor: 'rgb(204, 179, 255)',
                            color: 'rgba(0, 0, 0, 0.87)',
                            "&:hover": {
                                backgroundColor: 'rgb(230, 204, 255)',
                                color: 'rgba(0, 0, 0, 0.87)',
                            },
                        },
                        "& .row-disqualified": {
                            backgroundColor: 'rgb(255, 194, 153)',
                            color: 'rgba(0, 0, 0, 0.87)',
                            "&:hover": {
                                backgroundColor: 'rgb(255, 214, 179)',
                                color: 'rgba(0, 0, 0, 0.87)',
                            },
                        },
                        "& .row-refunded": {
                            backgroundColor: 'rgb(153, 204, 255)',
                            color: 'rgba(0, 0, 0, 0.87)',
                            "&:hover": {
                                backgroundColor: 'rgb(179, 214, 255)',
                                color: 'rgba(0, 0, 0, 0.87)',
                            },
                        },
                        "& .row-active": {
                            backgroundColor: 'sepia.background',
                            "&:hover": {
                                backgroundColor: 'rgba(235, 235, 235, 1)',
                            },
                        },
                        "& .MuiDataGrid-row.Mui-selected": {
                            backgroundColor: 'rgba(235, 235, 235, 1)',
                            "&:hover": {
                                backgroundColor: 'rgba(235, 235, 235, 1)',
                            },
                        },
                                                // Pinned columns will inherit row background colors through theme
                    }}
                />
            </Box>
            <Popover
                open={openPopper}
                anchorEl={document.getElementById('tournament-builder-footer')}
                onClose={() => setOpenPopper(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                sx={{
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}  
                PaperProps={{
                    sx: {
                        backgroundColor: 'white',
                        margin: 'auto 3%', // Center horizontally
                        width: '90vw', // Ensure it has a defined width,
                        height: '90vh',
                        padding: 2,
                    }
                }}
            >
                    <Stack 
                        direction='column' 
                        spacing={1} 
                        sx={{ 
                            padding: '20px', 
                            backgroundColor: 'white', 
                            //border: `1px solid ${appTheme.palette.primary.light}`,
                            borderRadius: 1,
                            //boxShadow: 3,
                            width: `calc(100% - 40px)`,
                            height: `calc(100% - 40px)`,
                            //transform: 'translateY(100%)',
                            //overflow: 'auto'
                        }}
                    >
                        <Stack direction='row' spacing={1} justifyContent='space-between'>
                            <Typography variant="h6" flex={1}>Registration Upload Preview</Typography>
                            <Stack direction='row' spacing={1}>
                                <Button onClick={() => handleFileUpload()}
                                    sx = {{
                                        width: 'max-content',
                                        borderRadius: 2,
                                        padding: 0.5,
                                        justifyContent: 'flex-start',
                                        border: '1px solid transparent',
                                        '&:hover': {border: `1px solid ${appTheme.palette.primary.extraLight}`},
                                        '&:active': {backgroundColor: 'transparent'},
                                        '&:focus': {backgroundColor: 'transparent'},
                                    }}
                                >Upload</Button>
                                {templateButton()}
                                <Button onClick={() => setFileData([])}
                                    sx = {{
                                        width: 'max-content',
                                        borderRadius: 2,
                                        padding: 0.5,
                                        justifyContent: 'flex-start',
                                        border: '1px solid transparent',
                                        '&:hover': {border: `1px solid ${appTheme.palette.primary.extraLight}`},
                                        '&:active': {backgroundColor: 'transparent'},
                                        '&:focus': {backgroundColor: 'transparent'},
                                    }}
                                >Clear</Button>
                                <Button onClick={() => setOpenPopper(false)}
                                    sx = {{
                                        width: 'max-content',
                                        borderRadius: 2,
                                        padding: 0.5,
                                        justifyContent: 'flex-start',
                                        border: '1px solid transparent',
                                        '&:hover': {border: `1px solid ${appTheme.palette.primary.extraLight}`},
                                        '&:active': {backgroundColor: 'transparent'},
                                        '&:focus': {backgroundColor: 'transparent'},
                                    }}
                                >Close</Button>
                            </Stack>
                        </Stack>
                        <Stack direction='row' spacing={4} width='100%' height='100%'>
                            <Box 
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log('Dragging', internalDragging, isDragging);
                                    if (!internalDragging && !isDragging) {
                                        setIsDragging(true);
                                    }
                                }}
                                onDragLeave={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!internalDragging) {
                                        setIsDragging(false);
                                    }
                                }}
                                onDrop={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!internalDragging) {
                                        setIsDragging(false);
                                        const files = e.dataTransfer.files;
                                        if (files && files.length > 0) {
                                            readFile(files[0]);
                                        }
                                    }
                                }}
                                sx={{
                                    border: `2px dashed ${isDragging ? appTheme.palette.primary.main : appTheme.palette.grey[400]}`,
                                    borderRadius: 2,
                                    padding: 2,
                                    backgroundColor: isDragging ? appTheme.palette.grey[100] : 'transparent',
                                    transition: 'background-color 0.3s ease',
                                    flex: 3,
                                    width: '75%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {fileData.length > 0 && !isDragging ? (
                                    <Stack direction='column' spacing={1} flex={3} overflow='auto'
                                        sx={{
                                            //border: `2px dashed ${isDragging ? appTheme.palette.primary.main : appTheme.palette.grey[400]}`,
                                            //borderRadius: 2,
                                            //padding: 2,
                                            height: '100%'
                                        }}
                                    >
                                        <Stack direction='row' spacing={1}>
                                        {Object.keys(fileData[0]).map((key: string) => (
                                            <Box sx={{ flex: 1, minWidth: 200, fontWeight: 500, whiteSpace: 'pre-wrap', borderRadius: 1,
                                                backgroundColor: getTemplate()?.requiredFields.find((field: any) => field.key === key)
                                                || getTemplate()?.optionalFields.find((field: any) => field.key === key)
                                                ? appTheme.palette.primary.extraLight : 'lightgray',
                                                padding: '5px 10px'
                                            }}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (internalDragging) {
                                                    setIsDragging(false);
                                                    setInternalDragging(false);
                                                    const data = e.dataTransfer.getData('text/plain'); // Parse the field data
                                                    handleDataFieldDrop(data, key); // Call the function with the dropped field
                                                }
                                            }}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap', textWrap: 'wrap', wordWrap: 'break-word' }}>
                                                    {getTemplate()?.requiredFields.find((field: any) => field.key === key)?.label 
                                                    || getTemplate()?.optionalFields.find((field: any) => field.key === key)?.label || key}
                                                </Typography>
                                            </Box>
                                        ))}
                                        </Stack>
                                        {fileData.map((row: any) => (
                                            <Stack direction='row' spacing={1}>
                                                {Object.keys(row).map((key: string) => (
                                                    <Typography variant="body2" sx={{ minWidth: 220, flex: 1, whiteSpace: 'pre-wrap', textIndent: '10px' }}>
                                                        {row[key]}
                                                    </Typography>
                                                ))}
                                            </Stack>
                                        ))}
                                    </Stack>
                                ):(
                                
                                    <Typography variant="body2" color={isDragging ? appTheme.palette.primary.main : 'text.secondary'}>
                                        {isDragging ? "Drop your file here" : "Drag and drop a file here, or click to upload"}
                                    </Typography>
                                
                                )}
                            </Box>
                            <Stack direction='column' spacing={1} flex={1} overflow='auto' height={`calc(100% - 50px)`}>
                                <Typography variant="h6">Data Fields</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap' }}>Required Fields</Typography>
                                {getTemplate()?.requiredFields.map((field: any) => (
                                    <Stack key={field.label} direction='row' spacing={1} justifyContent='space-between' alignItems='center'
                                    sx={{
                                        width: '80%',
                                        //border: '1px solid black',
                                        border: fileData.some((row: any) => row[field.key]) 
                                            ? `1px solid ${appTheme.palette.primary.main}` : `1px dashed ${appTheme.palette.error.main}`,
                                        backgroundColor: fileData.some((row: any) => row[field.key]) 
                                            ? appTheme.palette.primary.extraLight : appTheme.palette.error.light,
                                        borderRadius: 1, 
                                        padding: '5px 10px',
                                        }}
                                    draggable
                                    onDragStart={(e) => {
                                        setInternalDragging(true);
                                        //console.log(field);
                                        e.dataTransfer.setData('text/plain', JSON.stringify(field));
                                        //e.dataTransfer.setDragImage(createDragImage(field), 0, 0);
                                    }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        const data = e.dataTransfer.getData('text/plain');
                                        setInternalDragging(false);
                                    }}
                                    >
                                        <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                                            {field.label}
                                        </Typography>
                                        {fileData.some((row: any) => row[field.key]) ? <CheckCircleOutlinedIcon sx={{ fontSize: 16*factor }} /> : null}
                                    </Stack>
                                ))}
                                <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap' }}>Optional Fields</Typography>
                                {getTemplate()?.optionalFields.map((field: any) => (
                                    <Stack key={field.label} direction='row' spacing={1} justifyContent='space-between' alignItems='center'
                                    sx={{
                                        width: '80%', 
                                        border: fileData.some((row: any) => row[field.key]) 
                                            ? `1px solid ${appTheme.palette.primary.main}` : `1px dashed darkgrey`,
                                        backgroundColor: fileData.some((row: any) => row[field.key]) 
                                            ? appTheme.palette.primary.extraLight : 'lightgray',
                                        borderRadius: 1, 
                                        padding: '5px 10px',
                                    }}
                                    draggable
                                    onDragStart={(e) => {
                                        setInternalDragging(true);
                                        //console.log(field);
                                        e.dataTransfer.setData('text/plain', JSON.stringify(field));
                                        //e.dataTransfer.setDragImage(createDragImage(field), 0, 0);
                                    }}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        const data = e.dataTransfer.getData('text/plain');
                                        setInternalDragging(false);
                                    }}>
                                    <Typography variant="body2" 
                                        sx={{ flex: 1, fontWeight: 500 }}
                                    >
                                        {field.label}
                                    </Typography>
                                    {fileData.some((row: any) => row[field.key]) ? <CheckCircleOutlinedIcon sx={{ fontSize: 16*factor }} /> : null}
                                </Stack>
                                ))}
                            </Stack>
                        </Stack>
                    </Stack>
            </Popover>

            <Dialog
                open={importDialogOpen}
                onClose={() => !importing && setImportDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { minHeight: 400 } }}
            >
                <DialogTitle>Import Players</DialogTitle>
                <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
                    <Stack direction="row" spacing={0.5} sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                        <Button size="small" onClick={handleSelectAllImport}>
                            Select All
                        </Button>
                        <Button size="small" onClick={handleDeselectAllImport}>
                            Deselect All
                        </Button>
                    </Stack>
                    <Box
                        sx={{
                            overflow: 'auto',
                            flex: 1,
                            minHeight: 200,
                            maxHeight: 400,
                            py: 0.5,
                        }}
                    >
                        {[
                            { label: 'Not Registered', players: importNotRegistered },
                            { label: 'Registered', players: importRegistered },
                        ].map(({ label, players }) =>
                            players.length === 0 ? null : (
                            <Box key={label} sx={{ mb: 1 }}>
                                <Typography variant="caption" sx={{ px: 2, py: 0.25, display: 'block', fontWeight: 600, color: 'text.secondary' }}>
                                    {label}
                                </Typography>
                                {players.map((p) => {
                                    const key = playerKey(p);
                                    return (
                                        <Stack
                                            key={key}
                                            direction="row"
                                            alignItems="center"
                                            spacing={0.5}
                                            sx={{
                                                py: 0.25,
                                                px: 2,
                                                '&:hover': { backgroundColor: 'action.hover' },
                                            }}
                                        >
                                            <Checkbox
                                                checked={selectedPlayerKeys.has(key)}
                                                onChange={() => handleToggleImportPlayer(key)}
                                                size="small"
                                                sx={{ p: 0.25 }}
                                            />
                                            <Typography variant="body2" sx={{ minWidth: 0, width: 'fit-content' }}>
                                                {p.playerName ?? '—'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 0, width: 'fit-content' }}>
                                                {p.playerEmail ?? '—'}
                                            </Typography>
                                            {p.playerHandicap != null && (
                                                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                                    Hdcp: {p.playerHandicap}
                                                </Typography>
                                            )}
                                        </Stack>
                                    );
                                })}
                            </Box>
                            ))
                        }
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 2, py: 1 }}>
                    <Button onClick={() => setImportDialogOpen(false)} disabled={importing}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleImportFromRoster}
                        disabled={importing || selectedPlayerKeys.size === 0}
                    >
                        {importing ? 'Importing...' : 'Import'}
                    </Button>
                </DialogActions>
            </Dialog>
            
        </Stack>
    )
}
